import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

// material-ui
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

// project-imports
import IconButton from 'components/@extended/IconButton';
import { ThemeMode } from 'config';
import { persistThemeMode } from 'contexts/ConfigContext';
import useConfig from 'hooks/useConfig';
import { clacUiGlobal as ui } from 'utils/uiGlobal';

// assets
import { Moon, Setting2, Sun1 } from 'iconsax-reactjs';

// ==============================|| HEADER CONTENT - THEME MODE ||============================== //

const MODE_OPTIONS = [
  { mode: ThemeMode.LIGHT, Icon: Sun1, labelId: 'theme.mode.light' },
  { mode: ThemeMode.DARK, Icon: Moon, labelId: 'theme.mode.dark' },
  { mode: ThemeMode.AUTO, Icon: Setting2, labelId: 'theme.mode.auto' }
] as const;

export default function ThemeModeToggle() {
  const intl = useIntl();
  const { mode } = useConfig();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  // El cambio de modo reconstruye el tema de toda la app. Si se aplica mientras el menú
  // sigue abierto, el trabajo de re-render compite con la animación de cierre y además
  // MUI marca el popover con aria-hidden con el foco todavía dentro del item. Se guarda
  // aquí el modo elegido y se aplica cuando el menú ya terminó de cerrarse.
  const pendingMode = useRef<ThemeMode | null>(null);

  const handleSelect = (nextMode: ThemeMode) => {
    pendingMode.current = nextMode;
    // Devolver el foco al botón antes de cerrar evita que el item quede enfocado
    // bajo un ancestro con aria-hidden.
    anchorRef.current?.focus();
    setOpen(false);
  };

  const applyPendingMode = () => {
    if (pendingMode.current === null) return;
    const nextMode = pendingMode.current;
    pendingMode.current = null;
    if (nextMode === mode) return;

    // MUI reconstruye el theme completo (createTheme + los 52 overrides) en cada cambio
    // de modo. Hacerlo en caliente deja clases/estilos huérfanos que se acumulan sesión
    // tras sesión y vuelven la navegación cada vez más lenta hasta refrescar, así que
    // recargamos para aplicar el modo desde un documento limpio.
    //
    // Importante: escribimos el modo directo en localStorage (persistThemeMode) en vez
    // de pasar por onChangeMode/el contexto. Si dejáramos que React aplicara el cambio,
    // el documento actual alcanza a re-renderizar con el theme a medio reconstruir antes
    // de que el reload lo reemplace — se ve un parpadeo de colores mixtos. Al no tocar
    // React, este documento nunca se entera del cambio; solo lo aplica el documento nuevo.
    persistThemeMode(nextMode);
    window.location.reload();
  };

  const current = MODE_OPTIONS.find((option) => option.mode === mode) ?? MODE_OPTIONS[0];
  const CurrentIcon = current.Icon;

  return (
    <>
      <IconButton
        color="secondary"
        variant="light"
        ref={anchorRef}
        aria-label={intl.formatMessage({ id: 'theme.mode.aria' })}
        aria-controls={open ? 'theme-mode-menu' : undefined}
        aria-haspopup="true"
        onClick={() => setOpen((prev) => !prev)}
        size="large"
        sx={{
          p: '4px',
          borderRadius: '6px',
          color: open ? ui.semantic.accent.main : ui.semantic.n[400],
          bgcolor: open ? ui.semantic.accent.bg : 'transparent',
          '&:hover': { bgcolor: ui.semantic.n[50], color: ui.semantic.n[950] }
        }}
      >
        <CurrentIcon variant="Bold" />
      </IconButton>
      <Menu
        id="theme-mode-menu"
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: { sx: { mt: 1, minWidth: 180, boxShadow: ui.shadow.modal } },
          transition: { onExited: applyPendingMode }
        }}
      >
        {MODE_OPTIONS.map(({ mode: optionMode, Icon, labelId }) => (
          <MenuItem key={optionMode} selected={optionMode === mode} onClick={() => handleSelect(optionMode)}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Icon size={18} variant="Bold" />
            </ListItemIcon>
            <ListItemText primary={intl.formatMessage({ id: labelId })} slotProps={{ primary: { sx: { fontSize: '0.8125rem' } } }} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
